import map from 'lodash/map'
import type { ReactNode } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import { ButtonGroup, Dropdown } from 'react-bootstrap'
import { AdvanceFilterMenuItem } from './Advance'
import { Icon, TooltipPopOver } from '@ztechapps/webui.util'
import { useContext as useContextParent } from '../Context'
import type { ListField } from '@ztechapps/common.hook'
import { showAllFilterTemplate, useInitialFilterTemplate } from '@ztechapps/common.hook'
import { generateId, truncate } from '@ztechapps/common.utils'
import type { Template } from '@ztechapps/common.contract'
import { ResetFilterMenuItem } from './Reset'

type FilterMenuProps = {
  onFilter?: (filter: { template: Template }) => void
  category: string
  filterTemplate?: Template
  /** The template the parent applied last — mirrored into the local selection so switches
   * that bypass this menu's click path still move the label. */
  appliedTemplate?: Template
  filterFields?: ListField[]
  fields?: ListField[]
  advanceFilterMenuItem?: ReactNode
  resetFilterMenuItem?: ReactNode
}
export const FilterMenu = ({
  onFilter,
  category,
  filterTemplate,
  appliedTemplate,
  filterFields,
  fields,
  advanceFilterMenuItem,
  resetFilterMenuItem,
}: FilterMenuProps) => {
  const { searchable, loading: listLoading } = useContextParent() ?? {}

  filterFields = filterFields ?? fields

  const [selectedTemplate, setSelectedTemplate] = useState(showAllFilterTemplate)

  //if advanceFilterMenuItem was not declared then create new one based on teh the filter fields
  advanceFilterMenuItem = advanceFilterMenuItem ?? <AdvanceFilterMenuItem {...{ onFilter, category, filterFields }} />
  resetFilterMenuItem = resetFilterMenuItem ?? <ResetFilterMenuItem />

  const { filterTemplates, initialFilterTemplate } = useInitialFilterTemplate({
    category,
    providedFilterTemplate: filterTemplate,
  })

  const appliedTemplateIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    setSelectedTemplate(initialFilterTemplate)
    // Apply the resolved default/persisted template to the QUERY (not just the label),
    // mirroring the click path (filterNow), so an isDefault template filters on first load.
    // Skip the empty "Show All" pseudo-template. The ref guard applies each template id at
    // most once — preventing a re-fire / setState feedback loop when the resolved template
    // churns (e.g. a persisted filter from another context fighting the page default).
    const id = initialFilterTemplate?._id
    if (!id || id === showAllFilterTemplate._id) return
    if (appliedTemplateIdRef.current === id) return
    appliedTemplateIdRef.current = id
    onFilter?.({ template: initialFilterTemplate })
    // initialFilterTemplate (whole object) and onFilter intentionally excluded — narrowed to
    // initialFilterTemplate?._id exactly per the comment above: the ref guard already makes repeat
    // calls for the same id a no-op, but the intent is to react to the resolved template CHANGING,
    // not to the object's or the caller-supplied onFilter's identity churning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilterTemplate?._id])

  const filterNow =
    ({ template }) =>
    () => {
      onFilter?.({ template })
      setSelectedTemplate(template)
    }

  // A programmatic switch applies through onFilter without touching the local selection,
  // so mirror the applied template the parent reports — matched against the loaded list,
  // never re-firing onFilter (the caller already applied it).
  const appliedTemplateId = appliedTemplate?._id
  useEffect(() => {
    if (!appliedTemplateId) return
    const match = (filterTemplates ?? []).find(({ _id }) => _id === appliedTemplateId)
    if (match) setSelectedTemplate(match)
  }, [appliedTemplateId, filterTemplates])
  const templateName = selectedTemplate?.name ?? 'Show All'
  const dropdownId = useRef(generateId(3))

  return (
    <Dropdown as={ButtonGroup} id={`filterMenuDropdown_${dropdownId.current}`}>
      <TooltipPopOver tooltipBody={templateName}>
        <Dropdown.Toggle id={`dropdown-toggle-${dropdownId.current}`} variant="primary" disabled={listLoading}>
          <Icon name="filter" /> {searchable ? truncate(templateName, 20) : selectedTemplate?.name || 'Show All'}
        </Dropdown.Toggle>
      </TooltipPopOver>
      <Dropdown.Menu align={'end'}>
        {map(filterTemplates, (template, index) => {
          return (
            <Dropdown.Item key={template?._id ?? index} onClick={filterNow({ template })} eventKey={index + 1}>
              {template.name}
            </Dropdown.Item>
          )
        })}

        <Dropdown.Divider />

        {advanceFilterMenuItem}
        {resetFilterMenuItem}
      </Dropdown.Menu>
    </Dropdown>
  )
}
